import { API, graphqlOperation, Storage } from "aws-amplify";
import { v4 as uuidv4 } from "uuid";
import { createPricelist, updateUser, createAlert } from "../graphql/mutations";
import { getPricelist, getUser, listUsers } from "../graphql/queries";
import { getUserGroupAdjustments } from "./pricingUtils";

/**
 * Get customer offsets from user record (from UserGroup or individual offsets)
 * @param {Object} user - User record
 * @returns {Object} Object with percentageAdjustment and dollarAdjustment
 */
export const formatOffsetDisplay = (offsets) => {
  const parts = [];
  if (offsets.percentageAdjustment !== 0) {
    parts.push(
      `${offsets.percentageAdjustment > 0 ? "+" : ""}${offsets.percentageAdjustment}%`,
    );
  }
  if (offsets.dollarAdjustment !== 0) {
    parts.push(
      `${offsets.dollarAdjustment > 0 ? "+" : ""}$${Math.abs(offsets.dollarAdjustment).toFixed(2)}`,
    );
  }
  return parts.length > 0 ? parts.join(" + ") : "No offset";
};

/**
 * Get customer offsets from user record (from UserGroup or individual offsets)
 * @param {Object} user - User record
 * @returns {Object} Object with percentageAdjustment and dollarAdjustment
 */
export const getCustomerOffsets = async (user) => {
  try {
    // First check for UserGroup offsets
    if (user.userGroupID) {
      const groupAdjustments = await getUserGroupAdjustments(user.id);
      if (groupAdjustments.length > 0) {
        const group = groupAdjustments[0];
        return {
          percentageAdjustment: group.percentageAdjustment || 0,
          dollarAdjustment: group.dollarAdjustment || 0,
          adjustmentType: group.adjustmentType || "percentage",
        };
      }
    }

    // Fall back to individual user offsets
    if (
      user.priceOffsetType &&
      user.priceOffsetValue !== null &&
      user.priceOffsetValue !== undefined
    ) {
      return {
        percentageAdjustment:
          user.priceOffsetType === "percentage" ? user.priceOffsetValue : 0,
        dollarAdjustment:
          user.priceOffsetType === "fixed" ? user.priceOffsetValue : 0,
        adjustmentType: user.priceOffsetType,
      };
    }

    return {
      percentageAdjustment: 0,
      dollarAdjustment: 0,
      adjustmentType: "percentage",
    };
  } catch (error) {
    console.error("Error getting customer offsets:", error);
    return {
      percentageAdjustment: 0,
      dollarAdjustment: 0,
      adjustmentType: "percentage",
    };
  }
};

/**
 * Apply offsets to a price
 * @param {number} price - Original price
 * @param {Object} offsets - Object with percentageAdjustment and dollarAdjustment
 * @returns {number} Adjusted price
 */
export const applyOffsets = (price, offsets) => {
  const priceNum = parseFloat(price);
  if (isNaN(priceNum)) {
    return price;
  }

  let adjustedPrice = priceNum;

  // Apply percentage adjustment first
  if (offsets.percentageAdjustment) {
    adjustedPrice =
      adjustedPrice * (1 + parseFloat(offsets.percentageAdjustment) / 100);
  }

  // Then apply dollar adjustment
  if (offsets.dollarAdjustment) {
    adjustedPrice = adjustedPrice + parseFloat(offsets.dollarAdjustment);
  }

  return Math.round(adjustedPrice * 100) / 100; // Round to 2 decimal places
};

/**
 * Stack multiple offset levels (for sub-customers)
 * @param {number} price - Original price
 * @param {Array} offsetLevels - Array of offset objects to apply in order
 * @returns {number} Adjusted price after all offsets
 */
const applyStackedOffsets = (price, offsetLevels) => {
  let adjustedPrice = parseFloat(price);
  if (isNaN(adjustedPrice)) {
    return price;
  }

  for (const offsets of offsetLevels) {
    adjustedPrice = applyOffsets(adjustedPrice, offsets);
  }

  return Math.round(adjustedPrice * 100) / 100;
};

/**
 * Create a derived pricelist with offsets applied
 * @param {Object} sourcePricelist - Source pricelist object
 * @param {Object} targetUser - Target user to create pricelist for
 * @param {Array} offsetLevels - Array of offset objects to apply (for stacked offsets)
 * @param {string} sourceUserId - ID of the user pushing the pricelist
 * @returns {Promise<Object>} Created pricelist
 */
export const createDerivedPricelist = async (
  sourcePricelist,
  targetUser,
  offsetLevels,
  sourceUserId,
) => {
  try {
    // Fetch the source pricelist data from S3
    const sourceDataResponse = await Storage.get(sourcePricelist.s3Key, {
      download: true,
    });
    const sourceData = JSON.parse(await sourceDataResponse.Body.text());

    // Parse existing adjustments from source pricelist
    const sourceAdjustments = sourcePricelist.adjustments
      ? JSON.parse(sourcePricelist.adjustments)
      : { percentageAdjustment: 0, dollarAdjustment: 0 };

    // Apply offsets to each item in the pricelist
    const adjustedData = sourceData.map((item) => {
      // Start with the original price or adjustedPrice if available
      const basePrice =
        item.adjustedPrice !== undefined ? item.adjustedPrice : item.PRICE;

      // Apply stacked offsets
      const finalPrice = applyStackedOffsets(basePrice, offsetLevels);

      return {
        ...item,
        adjustedPrice: finalPrice,
        PRICE: finalPrice, // Update PRICE as well for consistency
      };
    });

    // Upload adjusted data to S3
    const newS3Key = `csv-data/${uuidv4()}.json`;
    await Storage.put(newS3Key, JSON.stringify(adjustedData), {
      contentType: "application/json",
    });

    // Calculate combined adjustments for the derived pricelist
    // This is for tracking purposes - actual prices are already adjusted
    const combinedAdjustments = offsetLevels.reduce(
      (acc, level) => {
        return {
          percentageAdjustment:
            acc.percentageAdjustment + (level.percentageAdjustment || 0),
          dollarAdjustment:
            acc.dollarAdjustment + (level.dollarAdjustment || 0),
        };
      },
      { percentageAdjustment: 0, dollarAdjustment: 0 },
    );

    // Create derived pricelist record
    // Keep userId as the original owner (sourceUserId) so it shows up in their manage pricelists
    // The targetUser will use it via activePricelistID - they don't need to own it
    const derivedPricelistInput = {
      userId: sourceUserId, // Keep as original owner so it shows in SuperAdmin's manage list
      providerId: sourceUserId, // Provider is also the source user
      s3Key: newS3Key,
      validRows: sourcePricelist.validRows,
      dataCount: sourcePricelist.dataCount,
      filename: sourcePricelist.filename,
      name: `${sourcePricelist.name} (for ${targetUser.username || targetUser.email})`,
      adjustments: JSON.stringify(combinedAdjustments),
      notes: `Derived from ${sourcePricelist.name} - pushed to ${targetUser.username || targetUser.email}`,
      pricelistDerivedPricelistsId: sourcePricelist.id, // This links to parent pricelist
      status: "active",
    };

    const createResponse = await API.graphql(
      graphqlOperation(createPricelist, { input: derivedPricelistInput }),
    );

    return createResponse.data.createPricelist;
  } catch (error) {
    console.error("Error creating derived pricelist:", error);
    throw error;
  }
};

/**
 * Get all customers and sub-customers recursively
 * @param {string} userId - User ID to get customers for
 * @param {number} maxDepth - Maximum depth to recurse (default: 10)
 * @returns {Promise<Array>} Array of customer objects with depth info
 */
export const getCustomerHierarchy = async (userId, maxDepth = 10) => {
  const customers = [];
  const processed = new Set();

  const fetchCustomers = async (parentId, depth = 0) => {
    if (depth >= maxDepth || processed.has(parentId)) {
      return;
    }

    processed.add(parentId);

    try {
      const response = await API.graphql(
        graphqlOperation(listUsers, {
          filter: {
            createdByUserId: { eq: parentId },
          },
          limit: 1000,
        }),
      );

      const directCustomers = response.data.listUsers.items || [];

      // Fetch userGroup data for each customer
      for (const customer of directCustomers) {
        if (customer.userGroupID) {
          try {
            const userResponse = await API.graphql(
              graphqlOperation(getUser, { id: customer.id }),
            );
            if (userResponse.data.getUser.userGroup) {
              customer.userGroup = userResponse.data.getUser.userGroup;
            }
          } catch (error) {
            console.error(
              `Error fetching userGroup for ${customer.id}:`,
              error,
            );
          }
        }

        customers.push({
          ...customer,
          depth,
          parentId,
        });

        // Recursively fetch sub-customers
        await fetchCustomers(customer.id, depth + 1);
      }
    } catch (error) {
      console.error(`Error fetching customers for ${parentId}:`, error);
    }
  };

  await fetchCustomers(userId);
  return customers;
};

/**
 * Create notification for pricelist update
 * @param {string} customerId - Customer ID to notify
 * @param {string} pricelistName - Name of the pricelist
 * @param {string} providerId - ID of the provider pushing the pricelist
 * @returns {Promise<Object>} Created alert
 */
export const createPricelistUpdateNotification = async (
  customerId,
  pricelistName,
  providerId,
) => {
  try {
    // Get provider info for the notification
    const providerResponse = await API.graphql(
      graphqlOperation(getUser, { id: providerId }),
    );
    const provider = providerResponse.data.getUser;
    const providerName =
      provider?.username || provider?.companyName || "Your provider";

    const alertInput = {
      title: "Pricelist Updated",
      content: `Your pricelist "${pricelistName}" has been updated by ${providerName}.`,
      userID: providerId, // Set to provider ID so notification shows up in customer's notification feed (based on NotificationContext logic)
      read: false,
      createdAt: new Date().toISOString(),
      isAdminNotification: false,
    };

    const response = await API.graphql(
      graphqlOperation(createAlert, { input: alertInput }),
    );

    return response.data.createAlert;
  } catch (error) {
    console.error("Error creating notification:", error);
    throw error;
  }
};
